// packages/auth/src/safeRedirect.ts

/**
 * Rotas que NUNCA devem ser destino de redirect pós-login para sessoes
 * authenticated (admin / analyst / consultor). Cobrem:
 *
 *   - `/p/:token/*` — magic-link cliente (escopo PublicWriteToken). Renderizar
 *     essas rotas com uma sessao admin/analyst ativa nao causa erro (sao
 *     publicas) mas confunde o usuario: o app salta de `/admin/...` para uma
 *     pagina mobile-first do cliente final com JWT na URL.
 *   - `/quote/:slug` + `/cotacao/:slug` — landing publica de cotacao do
 *     consultor. Mesma logica: nunca queremos jogar admin/analyst la.
 *   - `/login` + `/auth/*` + `/first-access` + `/forgot-password` — auth pages.
 *     Voltar para elas apos login completo gera loop ou navegacao circular.
 *   - `/registration`, `/registro*`, `/cadastro` — fluxo publico de cadastro.
 *
 * Helper usado pelos handlers de post-login (LoginForm, TwoFactorPage) e pelo
 * AuthGuard (na hora de capturar `state.from` para retornar depois) para
 * impedir que `/p/<jwt>/pendencias` (ou qualquer publica) acabe sendo o
 * destino apos uma sessao authenticated ser estabelecida.
 */
const UNSAFE_REDIRECT_PREFIXES = [
  '/p/',
  '/quote',
  '/cotacao',
  '/login',
  '/auth/',
  '/first-access',
  '/forgot-password',
  '/registration',
  '/registro',
  '/cadastro',
] as const;

const UNSAFE_REDIRECT_EXACT = new Set<string>(['/p', '/quote', '/cotacao']);

export function isUnsafePostLoginPath(pathname: string | null | undefined): boolean {
  if (!pathname || typeof pathname !== 'string') return true;
  if (!pathname.startsWith('/')) return true;
  if (UNSAFE_REDIRECT_EXACT.has(pathname)) return true;
  return UNSAFE_REDIRECT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Resolve para onde mandar o usuario apos autenticacao bem-sucedida.
 * Garante que rotas publicas nunca vencam o fallback `/dashboard`.
 */
export function pickPostLoginRedirect(
  candidate: string | null | undefined,
  fallback = '/dashboard',
): string {
  if (isUnsafePostLoginPath(candidate)) return fallback;
  return candidate as string;
}
